'use client'

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { Button } from '@open-mercato/ui/primitives/button'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type EmissionDetail = {
  id: string
  dispatchId: string | null
  legId: string | null
  vehicleId: string | null
  cargoId: string | null
  routeId: string | null
  transportDocumentId: string | null
  calculationMode: string
  roadKm: number
  freightWeightKg: number
  freightTonneKm: number
  totalCo2Kg: number
  methodology: string
  vehicleEuroClass: string | null
  fuelType: string | null
  appliedFactorKgPerTkm: number
  factorKey: string
  calculationTrace: Record<string, unknown> | null
  generatedAt: string
}

type DetailProps = {
  params: {
    id: string
  }
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '—'
  return JSON.stringify(value, null, 2)
}

export default function EmissionDetailPage({ params }: DetailProps) {
  const t = useT()
  const [item, setItem] = React.useState<EmissionDetail | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await apiCall<EmissionDetail>(`/api/freight/emissions/${encodeURIComponent(params.id)}`)
        if (!response.ok || !response.result) {
          throw new Error(t('emissions.pages.detail.loadError', 'Failed to load emission report.'))
        }
        if (!mounted) return
        setItem(response.result)
      } catch (loadError) {
        if (!mounted) return
        setError(loadError instanceof Error ? loadError.message : t('emissions.pages.detail.loadError', 'Failed to load emission report.'))
      } finally {
        if (mounted) setLoading(false)
      }
    }
    void load()
    return () => {
      mounted = false
    }
  }, [params.id, t])

  return (
    <Page>
      <PageBody>
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-muted-foreground">
                {t(`emissions.lookups.calculationMode.${item?.calculationMode ?? 'PLANNED'}`, item?.calculationMode ?? 'PLANNED')}
              </div>
              <h1 className="text-2xl font-semibold">
                {item?.id ?? t('emissions.pages.detail.title', 'Emission report')}
              </h1>
            </div>
            <Button asChild variant="outline">
              <Link href="/backend/emissions">{t('emissions.pages.detail.back', 'Back to emissions')}</Link>
            </Button>
          </div>

          {loading ? <LoadingMessage label={t('emissions.pages.detail.loading', 'Loading emission report…')} /> : null}
          {error ? <ErrorMessage label={error} /> : null}

          {!loading && !error && item ? (
            <div className="grid gap-6 lg:grid-cols-2">
              <section className="rounded-xl border p-4">
                <h2 className="text-base font-semibold">{t('emissions.detail.sections.summary', 'Summary')}</h2>
                <dl className="mt-4 grid gap-3 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.table.columns.roadKm', 'Road km')}</dt>
                    <dd>{item.roadKm.toFixed(3)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.table.columns.freightWeightKg', 'Freight kg')}</dt>
                    <dd>{item.freightWeightKg.toFixed(3)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.freightTonneKm', 'Freight tonne-km')}</dt>
                    <dd>{item.freightTonneKm.toFixed(6)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.table.columns.totalCo2Kg', 'CO2 kg')}</dt>
                    <dd>{item.totalCo2Kg.toFixed(6)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.generatedAt', 'Generated at')}</dt>
                    <dd>{new Date(item.generatedAt).toLocaleString()}</dd>
                  </div>
                </dl>
              </section>

              <section className="rounded-xl border p-4">
                <h2 className="text-base font-semibold">{t('emissions.detail.sections.references', 'References')}</h2>
                <dl className="mt-4 grid gap-3 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.table.columns.dispatch', 'Dispatch')}</dt>
                    <dd>{renderValue(item.dispatchId)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.table.columns.leg', 'Leg')}</dt>
                    <dd>{renderValue(item.legId)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.vehicle', 'Vehicle')}</dt>
                    <dd>{renderValue(item.vehicleId)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.cargo', 'Cargo')}</dt>
                    <dd>{renderValue(item.cargoId)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.route', 'Route')}</dt>
                    <dd>{renderValue(item.routeId)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.transportDocument', 'Transport document')}</dt>
                    <dd>{renderValue(item.transportDocumentId)}</dd>
                  </div>
                </dl>
              </section>

              <section className="rounded-xl border p-4">
                <h2 className="text-base font-semibold">{t('emissions.detail.sections.methodology', 'Methodology')}</h2>
                <dl className="mt-4 grid gap-3 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.methodology', 'Methodology')}</dt>
                    <dd>{item.methodology}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.table.columns.vehicleEuroClass', 'Euro class')}</dt>
                    <dd>{renderValue(item.vehicleEuroClass)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.fuelType', 'Fuel type')}</dt>
                    <dd>{renderValue(item.fuelType)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.appliedFactor', 'Applied factor')}</dt>
                    <dd>{item.appliedFactorKgPerTkm.toFixed(6)}</dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <dt className="text-muted-foreground">{t('emissions.detail.fields.factorKey', 'Factor key')}</dt>
                    <dd>{item.factorKey}</dd>
                  </div>
                </dl>
              </section>

              <section className="rounded-xl border p-4">
                <h2 className="text-base font-semibold">{t('emissions.detail.sections.trace', 'Calculation trace')}</h2>
                <pre className="mt-4 overflow-auto rounded-lg bg-muted/40 p-3 text-xs leading-6">
                  {renderValue(item.calculationTrace)}
                </pre>
              </section>
            </div>
          ) : null}
        </div>
      </PageBody>
    </Page>
  )
}
